import { Link } from 'react-router';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { CheckCircle2, ChevronRight, Circle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { SetupChecklist, SetupItemId } from '@/hooks/useSetupChecklist';

const ITEM_LABELS: Record<SetupItemId, MessageDescriptor> = {
  childcare: msg({ id: 'dashboard.setup.item.childcare', message: 'Add a Kita location' }),
  group: msg({ id: 'dashboard.setup.item.group', message: 'Create a group' }),
  staffingModel: msg({ id: 'dashboard.setup.item.staffingModel', message: 'Save the staffing model' }),
  qrIban: msg({ id: 'dashboard.setup.item.qrIban', message: 'Enter the QR-IBAN for invoices' }),
  tariff: msg({ id: 'dashboard.setup.item.tariff', message: 'Create a tariff' }),
  holidays: msg({ id: 'dashboard.setup.item.holidays', message: 'Load the public holidays' }),
  employeeRole: msg({
    id: 'dashboard.setup.item.employeeRole',
    message: 'Give an employee a work contract with a role',
  }),
};

interface SetupChecklistCardProps {
  checklist: SetupChecklist;
  className?: string;
}

/**
 * "Set up your Kita": the first-day steps with a link to the page of each.
 * Renders only while a step is known to be missing, so a finished
 * organisation never sees it.
 */
export function SetupChecklistCard({ checklist, className }: SetupChecklistCardProps) {
  const { i18n } = useLingui();
  if (!checklist.hasOpen) return null;

  return (
    <Card className={className} data-testid="setup-checklist">
      <CardHeader className="flex flex-row flex-wrap items-baseline justify-between gap-x-4 gap-y-1 space-y-0">
        <CardTitle className="text-base">
          {i18n._({ id: 'dashboard.setup.title', message: 'Set up your Kita' })}
        </CardTitle>
        <p className="text-sm text-muted-foreground tabular-nums">
          {i18n._({
            id: 'dashboard.setup.progress',
            message: '{done} of {total} done',
            values: { done: checklist.doneCount, total: checklist.items.length },
          })}
        </p>
      </CardHeader>
      <CardContent className="px-3">
        <ul className="grid gap-0.5 md:grid-cols-2">
          {checklist.items.map((item) => {
            const done = item.state === 'done';
            const open = item.state === 'open';
            return (
              <li key={item.id}>
                <Link
                  to={item.href}
                  className="flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {done ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-success-on-surface" aria-hidden="true" />
                  ) : (
                    <Circle className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  )}
                  <span className={cn('flex-1', done && 'text-muted-foreground')}>
                    {i18n._(ITEM_LABELS[item.id])}
                  </span>
                  {done && (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {i18n._({ id: 'dashboard.setup.state.done', message: 'Done' })}
                    </span>
                  )}
                  {open && (
                    <span className="flex shrink-0 items-center gap-0.5 font-medium text-primary">
                      {i18n._({ id: 'dashboard.setup.action', message: 'Set up' })}
                      <ChevronRight className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}
